/**
 * The guided run, after the visitor has told us what they trade.
 *
 * It is one journey now rather than four parallel ones. The old chooser asked
 * "what do you want to see?", which is a question about our features; the
 * template picker asks what they trade, which is a question about them — and
 * the answer builds them a real dashboard for free before the guide says
 * anything. There is much less left to explain once the screen is already
 * theirs, so the run is short and points at things that are already on it.
 *
 * Every trigger resolves to instrumentation that already exists (the
 * onFeatureUsage observer, the widgetFilters diff). The guide observes the
 * product; nothing inside a widget reports to the guide.
 */
export type StepTrigger =
  | { kind: "featureUsage"; featureName: string; featureAction?: string }
  /** Any filter/highlight panel change on any widget, via the tab store. */
  | { kind: "widgetFilters" }
  /** The rail owns this step's UI and advances it itself. */
  | { kind: "manual" };

export type DemoStepId = "compare" | "highlight" | "popout" | "watchlist";

export type DemoStep = {
  id: DemoStepId;
  /** Imperative — what to do, not what the feature is called. */
  instruction: string;
  hint?: string;
  /**
   * Which widget to ring. "filtered" resolves to the template's narrowed
   * Headlines instance at runtime; a literal id is used as-is.
   */
  target?: "filtered" | "primary" | (string & {});
  /** Control inside the target widget to light up while this step is live. */
  control?: "highlight" | "filter" | "popout";
  /**
   * A destination the rail can take them to itself.
   *
   * The rail marks a nav item and then expects the visitor to go and find it,
   * which makes the guide a set of directions rather than a way through. Where
   * the next thing is simply "be on another page", the rail offers the button
   * for it — clicking through the guide should do what clicking around the app
   * would have done.
   *
   * Navigation only. Applying a watchlist and popping a panel out stay manual,
   * because those are the actions the demo exists to have them perform.
   */
  action?: { to: string; label: string };
  trigger: StepTrigger;
};

/**
 * Four beats, in the order they earn their keep:
 *
 * 1. compare   — the two panels side by side, which needs one sentence to be
 *                seen rather than looked at.
 * 2. watchlist — build one, in the builder, prefilled for their desk.
 * 3. highlight — apply the thing they just built. Cause and effect: this is
 *                why the builder comes first.
 * 4. popout    — the second-monitor moment, and the last thing before the ask.
 */
export const DEMO_STEPS: DemoStep[] = [
  {
    id: "compare",
    instruction: "The highlighted panel is the same feed, filtered",
    hint: "Both panels show Newsquawk headlines. The one on the left is unfiltered. The highlighted one has a category filter applied, so it only shows headlines tagged for your market.",
    target: "filtered",
    trigger: { kind: "manual" },
  },
  {
    // Before the filter step, not after it. Building a list and then watching
    // it mark up the feed is cause and effect; the previous order asked
    // people to apply a watchlist and only afterwards showed them where
    // watchlists come from, which is the same two screens in the order that
    // explains neither.
    id: "watchlist",
    instruction: "Open Watchlists in the sidebar",
    hint: "A watchlist is the terms you care about. The builder can assemble one from a plain-English description — it's marked in the sidebar, and pre-filled for your desk.",
    action: { to: "/watchlists-filters", label: "Open Watchlists" },
    trigger: { kind: "manual" },
  },
  {
    id: "highlight",
    instruction: "Apply that watchlist to the feed",
    hint: "The marked highlighter icon in the headlines panel header opens the highlight menu. Pick your watchlist there — highlighting marks matching headlines in place, and the funnel beside it hides everything else instead.",
    target: "primary",
    control: "highlight",
    trigger: { kind: "widgetFilters" },
  },
  {
    id: "popout",
    instruction: "Open a panel in its own window",
    hint: "The marked icon in the panel header opens it as a separate browser window, which can be moved to a second monitor. It stays connected to the same feed.",
    target: "primary",
    control: "popout",
    trigger: {
      kind: "featureUsage",
      featureName: "widget",
      featureAction: "popout",
    },
  },
];

/**
 * There is no audio step, and it isn't an oversight.
 *
 * Demo squawk playback is disabled at SquawkPlayerContext's
 * DEMO_PLAYBACK_ENABLED, and neither host can serve this audience:
 *
 *   audio.newsquawk.com:6443/api/squawk_history   401 Authentication required
 *   transcribe.newsquawk.com/api/squawk_history   401, OAuth2 bearer per its
 *                                                 own openapi.json
 *
 * The transcribe service declares OAuth2AuthorizationCodeBearer against the
 * dev Keycloak realm (see its public /api/config), and its `token` query
 * param is that same bearer, not a share link. So a logged-out visitor has no
 * route to audio at all, and no frontend change can create one — it needs
 * either a public delayed endpoint or a server-minted demo token.
 *
 * Hearing an analyst call the tape is the most persuasive thing we own, so
 * when playback is restored this belongs in the run: pick a channel, listen,
 * with `{ kind: "featureUsage", featureName: "squawk_player", featureAction:
 * "channel_select" }` as the trigger — that signal already fires.
 */
/**
 * The apply step, worded for someone still standing on the builder page.
 *
 * Creating the watchlist advances the run, but they are on the wrong page for
 * the instruction that follows — and "apply that watchlist to the feed" is not
 * actionable when the feed is not on screen. Once they are back on the
 * dashboard the step's own copy takes over.
 */
/**
 * The build step, worded for someone already standing on the builder page.
 *
 * "Open Watchlists in the sidebar" is not useful advice once they are looking
 * at it, and the rail's navigation button has nowhere to send them.
 */
export const BUILD_ON_BUILDER = {
  instruction: "Press \u201cBuild it for me\u201d",
  hint: "It is marked, and already filled in with a description of your desk. Building it here creates nothing \u2014 the demo has no account to save to \u2014 but it shows you exactly what you would get.",
  /**
   * ?describe=1 is what the card itself does. The guide's button has to
   * perform the action it is naming, not merely step past it — pressing
   * "Build it for me" in the rail and pressing it on the page now do the
   * same thing. BuilderCanvas consumes and clears the param.
   */
  action: { to: "/watchlists-filters?describe=1", label: "Build it for me" },
};

export const APPLY_FROM_BUILDER = {
  instruction: "Now go back to the dashboard",
  hint: "Your watchlist is waiting in the highlight menu of either headlines panel. Dashboard is marked in the sidebar, or use the button below.",
  action: { to: "/dashboard", label: "Back to the dashboard" },
};

export const COMPLETION = {
  headline: "That's the demo.",
  body: "You built a watchlist, applied it to the feed and put a panel on its own screen. All of it works the same way on a live account, without the 24-hour delay.",
};
